import { useEffect, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Loader2, ReceiptText } from 'lucide-react';
import { cn, showToast } from '@my/ui';
import { tmsApi, type Invoice, type InvoiceLine, type VatRoundingMethodValue } from '@my/tms-api';
import { useInvoice, useRaiseCreditNote } from '@my/tms-api/hooks';
import { useConsumeOrganisationSequence, useOrganisation } from '@my/directory-api/hooks';
import { DocumentPdf } from '@/features/settings/documents/render/DocumentPdf';
import { useInvoiceDocumentSource } from '../lib/useInvoiceDocumentSource';

/**
 * Credit all or part of an invoice, opened under its row on the Summary tab.
 *
 * <p>
 * Lines come only from the invoice, each pre-filled with what is still
 * creditable and capped there; VAT, nominal and category are the original's
 * and cannot change — a credit note mirrors what it reverses. A reason is
 * required (it prints). "Release for re-invoicing" hands the charges of FULLY
 * credited lines back to Create invoices — for a wrong customer or price —
 * and is off by default, so a goodwill credit leaves the order invoiced.
 * </p>
 */
export function CreditNoteBuilder({
  invoiceId,
  organisationId,
  onCancel,
  onDone,
}: {
  invoiceId: string;
  organisationId: string | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const query = useInvoice(invoiceId);
  if (query.isPending) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-slate-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
          Loading invoice…
        </p>
      </Shell>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-red-600">Could not load this invoice.</p>
      </Shell>
    );
  }
  return <Builder invoice={query.data} organisationId={organisationId} onCancel={onCancel} onDone={onDone} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="border-y-2 border-rose-200 bg-rose-50/40 p-3">{children}</div>;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const remainingOf = (l: InvoiceLine) => round2(l.net - (l.creditedNet ?? 0));

function Builder({
  invoice,
  organisationId,
  onCancel,
  onDone,
}: {
  invoice: Invoice;
  organisationId: string | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const organisationQuery = useOrganisation(organisationId);
  const roundingMethod: VatRoundingMethodValue = organisationQuery.data?.vatRoundingMethod ?? 'Total';
  const consumeSeq = useConsumeOrganisationSequence();
  const raise = useRaiseCreditNote(organisationId);
  const docSource = useInvoiceDocumentSource(organisationId);
  const iso = invoice.currencyIsoCode;

  const lines = useMemo(
    () => invoice.lines.slice().sort((a, b) => a.sequence - b.sequence).filter((l) => remainingOf(l) > 0),
    [invoice.lines],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(lines.map((l) => l.id)));
  const [amounts, setAmounts] = useState<Record<string, number>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, remainingOf(l)])),
  );
  useEffect(() => {
    setSelected(new Set(lines.map((l) => l.id)));
    setAmounts(Object.fromEntries(lines.map((l) => [l.id, remainingOf(l)])));
  }, [lines]);
  const [reason, setReason] = useState('');
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [release, setRelease] = useState(false);

  const chosen = lines.filter((l) => selected.has(l.id) && (amounts[l.id] ?? 0) > 0);
  const over = chosen.filter((l) => (amounts[l.id] ?? 0) > remainingOf(l) + 0.0001);
  // Only a line credited in full can hand its charge back.
  const releasable = chosen.some((l) => l.sourceChargeCostId && (amounts[l.id] ?? 0) === remainingOf(l));

  const subtotal = round2(chosen.reduce((s, l) => s + (amounts[l.id] ?? 0), 0));
  const vatTotal = useMemo(() => {
    const parts = chosen.map((l) => ({ net: amounts[l.id] ?? 0, rate: l.taxRatePct }));
    if (roundingMethod === 'Item') return round2(parts.reduce((s, p) => s + round2((p.net * p.rate) / 100), 0));
    const byRate = new Map<number, number>();
    for (const p of parts) byRate.set(p.rate, (byRate.get(p.rate) ?? 0) + p.net);
    return round2([...byRate.entries()].reduce((s, [rate, net]) => s + round2((net * rate) / 100), 0));
  }, [chosen, amounts, roundingMethod]);

  const busy = consumeSeq.isPending || raise.isPending;
  const blocker =
    chosen.length === 0
      ? 'Choose at least one line to credit.'
      : over.length > 0
        ? `More than is left to credit on: ${over.map((l) => l.description).join(', ')}.`
        : !reason.trim()
          ? 'Say why the credit note is raised.'
          : null;

  const submit = async () => {
    if (blocker || !organisationId) return;
    try {
      const seq = await consumeSeq.mutateAsync({ organisationId, type: 'CreditNote' });
      const note = await raise.mutateAsync({
        invoiceId: invoice.id,
        number: seq.formatted,
        reason: reason.trim(),
        issueDate,
        issueNow: true,
        releaseCharges: release && releasable,
        roundingMethod,
        lines: chosen.map((l) => ({ invoiceLineId: l.id, net: amounts[l.id] ?? 0 })),
      });
      // Store the PDF as the invoice flow does; the note exists if this fails.
      try {
        const template = docSource.templateFor('Credit');
        if (template) {
          const blob = await pdf(<DocumentPdf template={template} data={docSource.buildData(note)} />).toBlob();
          await tmsApi.uploadInvoicePdf(note.id, blob);
        }
      } catch {
        // ignore — it can be recreated from the layout on download
      }
      showToast({ type: 'success', message: `Credit note ${note.number} raised for ${invoice.number}.` });
      onDone();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error && err.message ? err.message : 'Could not raise the credit note.',
      });
    }
  };

  return (
    <Shell>
      <p className="mb-2 flex items-center gap-1.5 rounded-md bg-rose-100/70 px-3 py-1.5 text-[11px] font-medium text-rose-800">
        <ReceiptText className="h-3.5 w-3.5" />
        Credit note for {invoice.number} — untick lines or lower amounts for a partial credit. VAT, nominal and
        category are the invoice's own.
      </p>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_18rem]">
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          {lines.length === 0 ? (
            <p className="p-4 text-center text-xs italic text-slate-400">This invoice has been credited in full.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {lines.map((l) => {
                const on = selected.has(l.id);
                const left = remainingOf(l);
                const amount = amounts[l.id] ?? 0;
                const tooMuch = amount > left + 0.0001;
                return (
                  <li key={l.id} className={cn('flex items-center gap-2 px-3 py-1.5 text-xs', !on && 'opacity-50')}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(l.id)) next.delete(l.id);
                          else next.add(l.id);
                          return next;
                        })
                      }
                      className="accent-rose-600"
                    />
                    <span className="w-24 shrink-0 text-[11px] tabular-nums text-emerald-700">{l.orderNumber || '—'}</span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                      {l.description}
                      {l.products && <span className="block truncate text-[10px] font-normal text-slate-500">{l.products}</span>}
                    </span>
                    <span className="w-28 shrink-0 truncate text-[11px] text-slate-500" title="The invoice's VAT — locked">
                      {l.taxRateName ? `${l.taxRateName} (${l.taxRatePct}%)` : `${l.taxRatePct}%`}
                    </span>
                    <span className="w-24 shrink-0 text-right text-[10px] text-slate-400">
                      of {money(left, iso)}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={left}
                      step="0.01"
                      aria-label={`Amount to credit on ${l.description}`}
                      value={amount}
                      disabled={!on}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setAmounts((a) => ({ ...a, [l.id]: Number.isFinite(n) && n >= 0 ? round2(n) : 0 }));
                      }}
                      className={cn(
                        'w-24 shrink-0 rounded-sm border px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-rose-400',
                        tooMuch ? 'border-red-400 text-red-700' : 'border-slate-200',
                      )}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-2.5 rounded-md border border-slate-200 bg-white p-3 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Reason (printed)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="e.g. Damaged goods — goodwill; Invoiced to the wrong customer"
              className={cn(
                'rounded-sm border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-rose-400',
                reason.trim() ? 'border-slate-200' : 'border-amber-300',
              )}
            />
          </label>
          <label className="flex items-center justify-between gap-2">
            <span className="text-slate-500">Date</span>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="rounded-sm border border-slate-200 px-1.5 py-0.5 text-xs"
            />
          </label>
          <label
            className={cn('flex items-start gap-2', !releasable && 'opacity-50')}
            title={
              releasable
                ? 'The charges of lines credited in full go back to Create invoices, to be billed again.'
                : 'Only a line credited in full can be released.'
            }
          >
            <input
              type="checkbox"
              checked={release && releasable}
              disabled={!releasable}
              onChange={(e) => setRelease(e.target.checked)}
              className="mt-0.5 accent-rose-600"
            />
            <span className="text-slate-600">
              Release fully credited charges for re-invoicing
              <span className="block text-[10px] text-slate-400">
                Wrong customer or price. A credit note that releases cannot be cancelled.
              </span>
            </span>
          </label>

          <div className="mt-1 border-t border-slate-200 pt-2">
            <Row label="Subtotal" value={money(subtotal, iso)} />
            <Row label="VAT" value={money(vatTotal, iso)} />
            <Row label="Credit total" value={money(subtotal + vatTotal, iso)} strong />
          </div>

          {blocker && <p className="text-[11px] text-amber-700">{blocker}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded-sm px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !!blocker}
              className="inline-flex items-center gap-1 rounded-sm bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Raise credit note
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between py-0.5', strong ? 'font-semibold text-slate-800' : 'text-slate-600')}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function money(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: iso || 'EUR' }).format(v);
  } catch {
    return v.toFixed(2);
  }
}
